// "Línea en el tiempo" — detalle de UNA línea sobre un período.
//
// Pirámide invertida: respuesta (diagnóstico) → evidencia (KPIs + gráficos) →
// dato crudo (tablas, paros). Se llega desde C5 (Líneas / Cumplimiento) y desde
// el modal del dashboard (LineaHoraAHoraDialog).
//
// URL: /coordinador/linea/:lineaId?desde&hasta&granularidad
//   - :lineaId → useParams
//   - ?desde&hasta&granularidad → useSearchParams (default: granularidad 'semana',
//     y últimos 7 días hoy-6 → hoy si no hay desde/hasta).
//
// Secciones (de arriba a abajo):
//   1. Cabecera (semáforo + nombre + nav ‹ › + granularidad)
//   2. Franja de diagnóstico (frase compuesta)
//   3. KPIs
//   4. Producción en el tiempo (Gráfico / Tabla, con drill a horas)
//   5. Por SKU
//   6. Paros · cuándo y por qué (timeline + pareto)

import { useMemo, useState } from 'react';
import { Link, Navigate, useParams, useSearchParams } from 'react-router-dom';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  ComposedChart,
  LabelList,
  Line,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { EtiquetaValor, pasoEtiquetas } from '../../components/charts/value-labels';
import type {
  CoordinadorLineaTiempo,
  CoordinadorLineaTiempoQuery,
  EstadoSemaforo,
  GranularidadLineaTiempo,
  LineaTiempoSerieDia,
  LineaTiempoSerieHora,
} from 'schemas';
import { CoordinadorSidebar } from '../../components/layout/CoordinadorSidebar';
import { RoleLayout } from '../../components/layout/RoleLayout';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import { Icon } from '../../components/ui/icon';
import { SegmentedControl } from '../../components/ui/segmented-control';
import { Spinner } from '../../components/ui/spinner';
import { TBody, TD, TFoot, TH, THead, TR, Table } from '../../components/ui/table';
import { useCoordinadorLineaTiempo, useMe } from '../../lib/api/queries';
import { useAuth } from '../../lib/auth';
import { cn } from '../../lib/utils';
import { etiquetaUnidad } from '../../lib/unidades';
import { LineaHoraAHoraDialog } from './LineaHoraAHoraDialog';
import {
  claseTono,
  fmtDecimal,
  fmtGramos,
  fmtGUnidadSigno,
  fmtKgSigno,
  tonoSobrellenado,
} from './pesoFormato';

// =============================================================================
// Helpers de formato (es-CO)
// =============================================================================

function fmtNumero(n: number): string {
  return Math.round(n).toLocaleString('es-CO');
}

function fmtPct(n: number | null, decimals = 1): string {
  if (n === null) return '—';
  return `${n.toFixed(decimals)}%`;
}

/** Minutos totales → "Xh Ym" / "Xh" / "Ym". */
function fmtMinutos(min: number): string {
  const total = Math.round(min);
  if (total < 60) return `${total}m`;
  const h = Math.floor(total / 60);
  const m = total % 60;
  return m === 0 ? `${h}h` : `${h}h ${m}m`;
}

/** Horas decimales → "112h" o "6h 52m". */
function fmtHoras(horas: number): string {
  return fmtMinutos(horas * 60);
}

function fmtFechaCorta(iso: string): string {
  if (!iso || iso.length < 10) return iso;
  const [y, m, d] = iso.slice(0, 10).split('-').map(Number);
  if (!y || !m || !d) return iso;
  return new Intl.DateTimeFormat('es-CO', { day: 'numeric', month: 'short', timeZone: 'UTC' })
    .format(new Date(Date.UTC(y, m - 1, d)))
    .replace(/\.|,/g, '')
    .replace(/\s+/g, ' ')
    .trim();
}

/** Período legible: "1 jun – 7 jun" o "7 jun" si desde === hasta. */
function fmtPeriodo(desde: string, hasta: string): string {
  if (desde === hasta) return fmtFechaCorta(desde);
  return `${fmtFechaCorta(desde)} – ${fmtFechaCorta(hasta)}`;
}

/** Fecha local YYYY-MM-DD. */
function ymd(date: Date): string {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

/** Parse YYYY-MM-DD → Date local (mediodía para evitar saltos de DST). */
function parseYmd(iso: string): Date {
  const [y, m, d] = iso.slice(0, 10).split('-').map(Number);
  return new Date(y ?? 1970, (m ?? 1) - 1, d ?? 1, 12, 0, 0);
}

/** Días (inclusive) entre dos YYYY-MM-DD. */
function diasEntre(desde: string, hasta: string): number {
  const a = parseYmd(desde).getTime();
  const b = parseYmd(hasta).getTime();
  return Math.round((b - a) / 86_400_000) + 1;
}

// =============================================================================
// Semáforo
// =============================================================================

type Tone = 'ok' | 'warn' | 'crit' | 'neutral';

function semaforoTone(estado: EstadoSemaforo): Tone {
  switch (estado) {
    case 'verde':
      return 'ok';
    case 'amarillo':
      return 'warn';
    case 'rojo':
      return 'crit';
    default:
      return 'neutral';
  }
}

/** Semáforo derivado de % (para cells de tabla / pct sueltos). */
function semaforoPct(pct: number | null): Tone {
  if (pct === null) return 'neutral';
  if (pct >= 90) return 'ok';
  if (pct >= 70) return 'warn';
  return 'crit';
}

function toneText(tone: Tone): string {
  return tone === 'ok'
    ? 'text-ok'
    : tone === 'warn'
      ? 'text-warn'
      : tone === 'crit'
        ? 'text-crit'
        : 'text-fg-tertiary';
}

function toneDot(tone: Tone): string {
  return tone === 'ok'
    ? 'bg-ok'
    : tone === 'warn'
      ? 'bg-warn'
      : tone === 'crit'
        ? 'bg-crit'
        : 'bg-fg-tertiary';
}

function toneVar(tone: Tone): string {
  return tone === 'ok'
    ? 'var(--color-ok)'
    : tone === 'warn'
      ? 'var(--color-warn)'
      : tone === 'crit'
        ? 'var(--color-crit)'
        : 'var(--color-fg-tertiary)';
}

// =============================================================================
// Componente principal
// =============================================================================

const GRANULARIDADES: GranularidadLineaTiempo[] = ['hora', 'dia', 'semana', 'mes'];

function isGranularidad(v: string | null): v is GranularidadLineaTiempo {
  return v !== null && (GRANULARIDADES as string[]).includes(v);
}

export function LineaTiempoPage() {
  const { state, signOut } = useAuth();
  const { lineaId } = useParams<{ lineaId: string }>();
  const [searchParams, setSearchParams] = useSearchParams();

  // Ventana actual: lee de la URL, con defaults sensatos.
  const granularidad: GranularidadLineaTiempo = isGranularidad(searchParams.get('granularidad'))
    ? (searchParams.get('granularidad') as GranularidadLineaTiempo)
    : 'semana';

  const { desde, hasta } = useMemo(() => {
    const d = searchParams.get('desde');
    const h = searchParams.get('hasta');
    if (d && h) return { desde: d, hasta: h };
    // Default: últimos 7 días (hoy-6 → hoy).
    const hoy = new Date();
    const inicio = new Date(hoy);
    inicio.setDate(hoy.getDate() - 6);
    return { desde: ymd(inicio), hasta: ymd(hoy) };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  const query: CoordinadorLineaTiempoQuery = useMemo(
    () => ({ desde, hasta, granularidad, comparar: true }),
    [desde, hasta, granularidad],
  );

  const { data, isLoading, isError, error, refetch, isFetching } = useCoordinadorLineaTiempo(
    lineaId ?? null,
    query,
  );

  // Capacidad "Corrección de datos con motivo" — habilita el lápiz por fila.
  const me = useMe();
  const puedeCorregir = me.data?.ctx.puede_corregir_datos ?? false;

  // Fila en edición → abre el drill-down hora-a-hora en su fecha+turno.
  const [editar, setEditar] = useState<{ turnoId: string; fecha: string } | null>(null);

  // Corre la ventana por su propio tamaño (nav ‹ ›) actualizando ?desde&hasta.
  const moverVentana = (signo: 1 | -1) => {
    const span = diasEntre(desde, hasta);
    const nuevoDesde = parseYmd(desde);
    const nuevoHasta = parseYmd(hasta);
    nuevoDesde.setDate(nuevoDesde.getDate() + signo * span);
    nuevoHasta.setDate(nuevoHasta.getDate() + signo * span);
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.set('desde', ymd(nuevoDesde));
      next.set('hasta', ymd(nuevoHasta));
      return next;
    });
  };

  const cambiarGranularidad = (g: GranularidadLineaTiempo) => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.set('granularidad', g);
      return next;
    });
  };

  // Drill desde una fila de día → ese día en granularidad hora.
  const drillADia = (dia: string) => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.set('desde', dia);
      next.set('hasta', dia);
      next.set('granularidad', 'hora');
      return next;
    });
  };

  if (state.kind !== 'signed_in') return <Navigate to="/login" replace />;

  if (isLoading) {
    return (
      <RoleLayout sidebar={<CoordinadorSidebar />} roleLabel="Coordinador">
        <div className="flex min-h-[60vh] flex-col items-center justify-center gap-3">
          <Spinner className="size-6" />
          <p className="text-sm text-fg-tertiary">Cargando línea…</p>
        </div>
      </RoleLayout>
    );
  }

  if (isError || !data) {
    return (
      <RoleLayout sidebar={<CoordinadorSidebar />} roleLabel="Coordinador">
        <div className="mx-auto flex min-h-[60vh] max-w-md flex-col items-center justify-center gap-3 text-center">
          <h1 className="text-lg font-medium text-crit">No pudimos cargar la línea</h1>
          <p className="text-sm text-fg-secondary">
            {error instanceof Error ? error.message : 'Error desconocido'}
          </p>
          <Button onClick={() => refetch()} disabled={isFetching}>
            {isFetching ? 'Reintentando…' : 'Reintentar'}
          </Button>
          <button onClick={() => signOut()} className="text-xs text-fg-tertiary underline">
            Cerrar sesión
          </button>
        </div>
      </RoleLayout>
    );
  }

  return (
    <RoleLayout sidebar={<CoordinadorSidebar />} roleLabel="Coordinador">
      <div className="flex flex-col gap-4">
        <Link
          to="/coordinador/cumplimiento"
          className="inline-flex w-fit items-center gap-1 text-sm text-fg-secondary transition-colors hover:text-fg"
        >
          <Icon name="chevron-left" size={16} />
          Líneas / Cumplimiento
        </Link>
        <Cabecera
          data={data}
          granularidad={granularidad}
          onMover={moverVentana}
          onGranularidad={cambiarGranularidad}
        />
        <FranjaDiagnostico data={data} />
        <KpisRow data={data} />
        <ProduccionEnElTiempo
          data={data}
          onDrillDia={drillADia}
          puedeCorregir={puedeCorregir}
          onEditar={(turnoId, fecha) => setEditar({ turnoId, fecha })}
        />
        <PorSku data={data} />
        <ParosSection data={data} />
        <PersonalSection data={data} />
        <footer className="text-center text-[11px] text-fg-tertiary">
          {data.linea.codigo} · {data.linea.area_nombre} · {data.linea.planta_nombre}
        </footer>
      </div>

      {/* Drill-down de corrección — abre en la fecha+turno de la fila clickeada. */}
      <LineaHoraAHoraDialog
        linea={editar ? { linea_id: data.linea.id, linea_nombre: data.linea.nombre } : null}
        turnoId={editar?.turnoId ?? null}
        fecha={editar?.fecha ?? null}
        onClose={() => setEditar(null)}
      />
    </RoleLayout>
  );
}

// =============================================================================
// 1. Cabecera
// =============================================================================

function Cabecera({
  data,
  granularidad,
  onMover,
  onGranularidad,
}: {
  data: CoordinadorLineaTiempo;
  granularidad: GranularidadLineaTiempo;
  onMover: (signo: 1 | -1) => void;
  onGranularidad: (g: GranularidadLineaTiempo) => void;
}) {
  const tone = semaforoTone(data.estado_semaforo);
  return (
    <header className="flex flex-wrap items-end justify-between gap-3 border-b border-border pb-3">
      <div className="flex items-start gap-2.5">
        <span
          aria-hidden
          className={cn('mt-2 inline-block size-2.5 flex-shrink-0 rounded-full', toneDot(tone))}
        />
        <div>
          <h1 className="font-serif text-2xl font-medium leading-tight text-fg">
            {data.linea.nombre}
          </h1>
          <p className="mt-1 text-[11px] text-fg-tertiary">
            {data.linea.codigo} · {data.linea.area_nombre} ·{' '}
            <span className="text-fg-secondary">
              {fmtPeriodo(data.periodo.desde, data.periodo.hasta)}
            </span>
          </p>
        </div>
      </div>
      <div className="flex flex-wrap items-center gap-2">
        <div className="inline-flex items-center rounded border border-border bg-surface-overlay p-0.5">
          <button
            type="button"
            onClick={() => onMover(-1)}
            aria-label="Período anterior"
            className="flex h-7 w-7 items-center justify-center rounded text-fg-secondary hover:bg-surface-elevated hover:text-fg"
          >
            ‹
          </button>
          <button
            type="button"
            onClick={() => onMover(1)}
            aria-label="Período siguiente"
            className="flex h-7 w-7 items-center justify-center rounded text-fg-secondary hover:bg-surface-elevated hover:text-fg"
          >
            ›
          </button>
        </div>
        <SegmentedControl<GranularidadLineaTiempo>
          size="sm"
          value={granularidad}
          onChange={onGranularidad}
          options={[
            { value: 'hora', label: 'Hora' },
            { value: 'dia', label: 'Día' },
            { value: 'semana', label: 'Semana' },
            { value: 'mes', label: 'Mes' },
          ]}
        />
      </div>
    </header>
  );
}

// =============================================================================
// 2. Franja de diagnóstico
// =============================================================================

function FranjaDiagnostico({ data }: { data: CoordinadorLineaTiempo }) {
  const { diagnostico: d, kpis: k } = data;
  const tone = semaforoTone(data.estado_semaforo);
  const unidad = etiquetaUnidad(k.unidad);

  const accentBorder =
    tone === 'ok'
      ? 'border-l-ok'
      : tone === 'warn'
        ? 'border-l-warn'
        : tone === 'crit'
          ? 'border-l-crit'
          : 'border-l-fg-tertiary';

  return (
    <div
      className={cn(
        'rounded-md border border-l-4 border-border bg-surface-overlay/40 px-4 py-3',
        accentBorder,
      )}
    >
      <p className="text-sm leading-relaxed text-fg-secondary">
        Al{' '}
        <span className={cn('font-semibold', toneText(tone))}>{fmtPct(k.cumpl_meta_pct, 0)}</span>{' '}
        de la meta.
        {k.gap_total > 0 ? (
          <>
            {' '}
            De{' '}
            <span className="font-medium text-fg">
              {fmtNumero(k.gap_total)} {unidad}
            </span>{' '}
            perdidos: <span className="font-medium text-fg">~{Math.round(d.pct_por_paro)}%</span>{' '}
            por paros
            {d.causal_top ? (
              <>
                {' '}
                (<span className="text-fg">{d.causal_top.nombre}</span>{' '}
                {fmtMinutos(d.causal_top.minutos)})
              </>
            ) : null}
            , <span className="font-medium text-fg">~{Math.round(d.pct_por_ritmo)}%</span> por ritmo
            bajo.
          </>
        ) : (
          <> Sin brecha contra la meta en el período.</>
        )}
        {d.sku_top ? (
          <>
            {' '}
            El SKU <span className="font-medium text-fg">{d.sku_top.codigo}</span> es el que más
            arrastra (<span className="text-fg">{Math.round(d.sku_top.pct_aporte_gap)}%</span>).
          </>
        ) : null}
      </p>
    </div>
  );
}

// =============================================================================
// 3. KPIs
// =============================================================================

type DeltaKey = 'cumpl_meta_pct' | 'producido' | 'meta' | 'paro_min' | 'paros_count';

function KpisRow({ data }: { data: CoordinadorLineaTiempo }) {
  const { kpis: k, comparativo } = data;
  const metaTone = semaforoPct(k.cumpl_meta_pct);
  const unidad = etiquetaUnidad(k.unidad);

  return (
    <section className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6">
      <KpiCard
        label="Cumpl. meta"
        value={fmtPct(k.cumpl_meta_pct, 0)}
        valueTone={metaTone}
        delta={deltaInfo(comparativo, 'cumpl_meta_pct', 'pp', true)}
      />
      <KpiCard
        label="Producido"
        value={fmtNumero(k.producido)}
        suffix={` ${unidad}`}
        sub={`meta ${fmtNumero(k.meta)}`}
        delta={deltaInfo(comparativo, 'producido', 'num', true)}
      />
      <KpiCard
        label="Gap"
        value={fmtNumero(k.gap_total)}
        suffix={` ${unidad}`}
        valueTone={k.gap_total > 0 ? 'warn' : 'neutral'}
      >
        <GapMiniBar gapParo={k.gap_paro} gapRitmo={k.gap_ritmo} />
      </KpiCard>
      <KpiCard
        label="Paro"
        value={k.paro_min > 0 ? fmtMinutos(k.paro_min) : '—'}
        valueTone={k.paro_min > 0 ? 'crit' : 'neutral'}
        sub={`${k.paros_count} evento${k.paros_count === 1 ? '' : 's'}`}
        delta={deltaInfo(comparativo, 'paro_min', 'num', false)}
      />
      <KpiCard label="Horas de trabajo" value={fmtHoras(k.horas_efectivas)} />
      {k.golpes_aplica ? (
        <KpiCard
          label="Golpes/min"
          value={k.golpes_real !== null ? k.golpes_real.toFixed(0) : '—'}
          sub={k.golpes_estandar !== null ? `est. ${k.golpes_estandar.toFixed(0)}` : 'sin estándar'}
          valueTone={
            k.golpes_real !== null && k.golpes_estandar !== null && k.golpes_estandar > 0
              ? semaforoPct((k.golpes_real / k.golpes_estandar) * 100)
              : 'neutral'
          }
        />
      ) : null}
      {/* Peso por unidad: los campos solo existen si la línea tiene algún
          registro con kg en el período (líneas de máquina con báscula). */}
      {k.gramos_unidad_real !== undefined ? (
        <KpiCard
          label="Peso por unidad"
          value={k.gramos_unidad_real !== null ? fmtDecimal(k.gramos_unidad_real) : '—'}
          suffix={k.gramos_unidad_real !== null ? ' g' : undefined}
          sub={
            k.peso_nominal_g === null || k.peso_nominal_g === undefined
              ? 'sin peso nominal'
              : `nominal ${fmtGramos(k.peso_nominal_g)} · ${fmtGUnidadSigno(k.sobrellenado_g_unidad)}`
          }
          valueTone={tonoSobrellenado(k.sobrellenado_g_unidad)}
        />
      ) : null}
    </section>
  );
}

interface DeltaDisplay {
  direction: 'up' | 'down' | 'flat';
  text: string;
  tone: Tone;
}

/**
 * Construye el delta "vs período anterior" a partir del comparativo.
 *  - kind 'pp' → puntos porcentuales; 'num' → número de paquetes/etc.
 *  - mayorEsMejor: true para cumplimiento/producido, false para paros (subir = malo).
 */
function deltaInfo(
  comparativo: CoordinadorLineaTiempo['comparativo'],
  key: DeltaKey,
  kind: 'pp' | 'num',
  mayorEsMejor: boolean,
): DeltaDisplay | undefined {
  if (!comparativo) return undefined;
  const raw = comparativo.deltas[key];
  if (raw === null) return undefined;
  const direction = raw > 0 ? 'up' : raw < 0 ? 'down' : 'flat';
  const positivoBueno = mayorEsMejor ? raw > 0 : raw < 0;
  const tone: Tone = raw === 0 ? 'neutral' : positivoBueno ? 'ok' : 'crit';
  const signo = raw > 0 ? '+' : '';
  const valor = kind === 'pp' ? `${signo}${raw.toFixed(1)} pp` : `${signo}${fmtNumero(raw)}`;
  return { direction, text: valor, tone };
}

function KpiCard({
  label,
  value,
  suffix,
  sub,
  valueTone = 'neutral',
  delta,
  children,
}: {
  label: string;
  value: string;
  suffix?: string;
  sub?: string;
  valueTone?: Tone;
  delta?: DeltaDisplay;
  children?: React.ReactNode;
}) {
  return (
    <Card>
      <CardContent className="flex flex-col gap-1.5 p-3">
        <span className="text-[10px] font-medium uppercase tracking-[0.1em] text-fg-tertiary">
          {label}
        </span>
        <span
          className={cn(
            'text-[22px] font-semibold leading-none tabular-nums tracking-tight',
            valueTone === 'neutral' ? 'text-fg' : toneText(valueTone),
          )}
        >
          {value}
          {suffix ? <span className="text-[12px] text-fg-tertiary">{suffix}</span> : null}
        </span>
        {children}
        {sub ? <p className="text-[10.5px] text-fg-tertiary">{sub}</p> : null}
        {delta ? (
          <p className={cn('text-[10.5px] font-medium tabular-nums', toneText(delta.tone))}>
            {delta.direction === 'up' ? '↑' : delta.direction === 'down' ? '↓' : '→'} {delta.text}{' '}
            <span className="font-normal text-fg-tertiary">vs período ant.</span>
          </p>
        ) : null}
      </CardContent>
    </Card>
  );
}

/** Mini-barra que descompone el gap en paro (crit) vs ritmo (warn). */
function GapMiniBar({ gapParo, gapRitmo }: { gapParo: number; gapRitmo: number }) {
  const total = gapParo + gapRitmo;
  if (total <= 0) return null;
  const pParo = (gapParo / total) * 100;
  const pRitmo = (gapRitmo / total) * 100;
  return (
    <div className="mt-0.5 flex flex-col gap-1">
      <div className="flex h-1.5 w-full overflow-hidden rounded-full bg-surface-overlay">
        <div className="h-full bg-crit" style={{ width: `${pParo}%` }} title="Por paro" />
        <div className="h-full bg-warn" style={{ width: `${pRitmo}%` }} title="Por ritmo" />
      </div>
      <div className="flex justify-between text-[9.5px] text-fg-tertiary">
        <span>paro {fmtNumero(gapParo)}</span>
        <span>ritmo {fmtNumero(gapRitmo)}</span>
      </div>
    </div>
  );
}

// =============================================================================
// 4. Producción en el tiempo
// =============================================================================

type VistaProd = 'grafico' | 'tabla';
type EjeY = 'produccion' | 'cumplimiento';

function ProduccionEnElTiempo({
  data,
  onDrillDia,
  puedeCorregir,
  onEditar,
}: {
  data: CoordinadorLineaTiempo;
  onDrillDia: (dia: string) => void;
  puedeCorregir: boolean;
  onEditar: (turnoId: string, fecha: string) => void;
}) {
  // Default 'tabla': la tabla es el camino directo a editar (el lápiz vive ahí).
  // El usuario puede pasar a 'grafico' con el toggle si quiere la vista visual.
  const [vista, setVista] = useState<VistaProd>('tabla');

  return (
    <Card>
      <CardContent className="flex flex-col gap-3 p-3">
        <div className="flex flex-wrap items-baseline justify-between gap-2">
          <h2 className="text-sm font-medium text-fg">Producción en el tiempo</h2>
          <SegmentedControl<VistaProd>
            size="sm"
            value={vista}
            onChange={setVista}
            options={[
              { value: 'grafico', label: 'Gráfico' },
              { value: 'tabla', label: 'Tabla' },
            ]}
          />
        </div>
        {vista === 'grafico' ? (
          <ProduccionGrafico data={data} />
        ) : data.eje === 'dia' ? (
          <TablaDia serie={data.serie_dia} onDrillDia={onDrillDia} />
        ) : (
          <TablaHora serie={data.serie_hora} puedeCorregir={puedeCorregir} onEditar={onEditar} />
        )}
      </CardContent>
    </Card>
  );
}

/**
 * Serie horaria → datos del ComposedChart. Los bloques INVALIDADOS quedan
 * visibles en la tabla (con su badge) pero acá van en null: no aportan barra,
 * meta ni % — el KPI del panel tampoco los cuenta (V5, auditoría 2026-09-06).
 * Exportada para test de regresión.
 */
export function serieHoraAChartData(
  serie: Array<{
    hora: string;
    producido: number;
    meta_h: number;
    cumpl_meta_pct: number | null;
    estado: 'valido' | 'invalidado';
  }>,
): Array<{ label: string; producido: number | null; meta: number | null; cumpl: number | null }> {
  return serie.map((p) => {
    const invalidado = p.estado === 'invalidado';
    return {
      label: p.hora,
      producido: invalidado ? null : Math.round(p.producido),
      meta: invalidado ? null : Math.round(p.meta_h),
      cumpl: invalidado ? null : p.cumpl_meta_pct,
    };
  });
}

function ProduccionGrafico({ data }: { data: CoordinadorLineaTiempo }) {
  const [ejeY, setEjeY] = useState<EjeY>('produccion');
  const unidad = etiquetaUnidad(data.kpis.unidad);

  const chartData = useMemo(() => {
    if (data.eje === 'dia') {
      return data.serie_dia.map((p) => ({
        label: fmtFechaCorta(p.periodo),
        producido: Math.round(p.producido),
        meta: Math.round(p.meta),
        cumpl: p.cumpl_meta_pct,
      }));
    }
    return serieHoraAChartData(data.serie_hora);
  }, [data]);

  if (chartData.length === 0) {
    return <p className="py-10 text-center text-sm text-fg-tertiary">Sin datos en el período.</p>;
  }

  return (
    <div className="flex flex-col gap-2">
      <div className="flex justify-end">
        <SegmentedControl<EjeY>
          size="sm"
          value={ejeY}
          onChange={setEjeY}
          options={[
            { value: 'produccion', label: `Producción (${unidad})` },
            { value: 'cumplimiento', label: 'Cumplimiento (%)' },
          ]}
        />
      </div>

      <div className="h-[280px] w-full">
        <ResponsiveContainer width="100%" height="100%">
          <ComposedChart data={chartData} margin={{ top: 16, right: 16, left: 8, bottom: 8 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" vertical={false} />
            <XAxis
              dataKey="label"
              tick={{ fontSize: 10.5, fill: 'var(--color-fg-tertiary)' }}
              axisLine={{ stroke: 'var(--color-border)' }}
              tickLine={false}
              interval="preserveStartEnd"
            />
            <YAxis
              tickFormatter={(v) => (ejeY === 'cumplimiento' ? `${v}%` : fmtNumero(Number(v)))}
              domain={ejeY === 'cumplimiento' ? [0, 'auto'] : ['auto', 'auto']}
              tick={{ fontSize: 10.5, fill: 'var(--color-fg-tertiary)' }}
              axisLine={false}
              tickLine={false}
              width={48}
            />
            <Tooltip
              contentStyle={{
                background: 'var(--color-surface-elevated)',
                border: '1px solid var(--color-border)',
                borderRadius: 8,
                fontSize: 12,
              }}
              formatter={(value, name) => {
                if (value === null || value === undefined) return ['s/d', name as string];
                if (name === 'Cumplimiento') return [`${Number(value).toFixed(0)}%`, name];
                return [`${fmtNumero(Number(value))} ${unidad}`, name as string];
              }}
            />
            {ejeY === 'produccion' ? (
              <>
                <Bar
                  dataKey="producido"
                  name="Producido"
                  fill="var(--color-accent)"
                  fillOpacity={0.85}
                  radius={[3, 3, 0, 0]}
                >
                  <LabelList
                    content={
                      <EtiquetaValor
                        formatter={(v) => fmtNumero(v)}
                        paso={pasoEtiquetas(chartData.length)}
                        total={chartData.length}
                        fontSize={9.5}
                      />
                    }
                  />
                </Bar>
                <Line
                  type="monotone"
                  dataKey="meta"
                  name="Meta"
                  stroke="var(--color-fg-secondary)"
                  strokeWidth={2}
                  strokeDasharray="6 4"
                  dot={false}
                />
              </>
            ) : (
              <Line
                type="monotone"
                dataKey="cumpl"
                name="Cumplimiento"
                stroke="var(--color-accent)"
                strokeWidth={2}
                dot={{ r: 2, fill: 'var(--color-accent)' }}
                connectNulls
              >
                <LabelList
                  content={
                    <EtiquetaValor
                      formatter={(v) => `${v.toFixed(0)}%`}
                      paso={pasoEtiquetas(chartData.length)}
                      total={chartData.length}
                      dy={-10}
                    />
                  }
                />
              </Line>
            )}
          </ComposedChart>
        </ResponsiveContainer>
      </div>

      {/* Leyenda manual (forma + color) */}
      <div className="flex flex-wrap items-center justify-center gap-4 text-[10.5px] text-fg-tertiary">
        {ejeY === 'produccion' ? (
          <>
            <LegendItem shape="bar" color="var(--color-accent)" label="Producido" />
            <LegendItem shape="dashed" color="var(--color-fg-secondary)" label="Meta" />
          </>
        ) : (
          <LegendItem shape="line" color="var(--color-accent)" label="Cumplimiento meta" />
        )}
      </div>
    </div>
  );
}

function LegendItem({
  shape,
  color,
  label,
}: {
  shape: 'bar' | 'line' | 'dashed' | 'dotted';
  color: string;
  label: string;
}) {
  return (
    <span className="inline-flex items-center gap-1.5">
      {shape === 'bar' ? (
        <span className="inline-block h-2.5 w-2.5 rounded-sm" style={{ background: color }} />
      ) : (
        <span
          className="inline-block h-0 w-5"
          style={{
            borderTop: `2px ${shape === 'dashed' ? 'dashed' : shape === 'dotted' ? 'dotted' : 'solid'} ${color}`,
          }}
        />
      )}
      {label}
    </span>
  );
}

// =============================================================================
// 4b. Tabla por día (con drill a horas)
// =============================================================================

function TablaDia({
  serie,
  onDrillDia,
}: {
  serie: LineaTiempoSerieDia[];
  onDrillDia: (dia: string) => void;
}) {
  const totales = useMemo(() => {
    return serie.reduce(
      (acc, d) => {
        acc.producido += d.producido;
        acc.meta += d.meta;
        acc.paro_min += d.paro_min;
        return acc;
      },
      { producido: 0, meta: 0, paro_min: 0 },
    );
  }, [serie]);

  if (serie.length === 0) {
    return <p className="py-10 text-center text-sm text-fg-tertiary">Sin datos en el período.</p>;
  }

  const totMeta = totales.meta > 0 ? (totales.producido / totales.meta) * 100 : null;

  return (
    <Table maxHeight="70vh">
      <THead sticky>
        <TR>
          <TH>Día</TH>
          <TH align="right">Producido</TH>
          <TH align="right">Meta</TH>
          <TH align="right">% meta</TH>
          <TH align="right">Paro</TH>
          <TH>Top causal</TH>
        </TR>
      </THead>
      <TBody>
        {serie.map((d) => (
          <TR
            key={d.periodo}
            className="cursor-pointer hover:bg-surface-overlay/40"
            onClick={() => onDrillDia(d.periodo)}
            title="Ver horas de este día"
          >
            <TD>
              <span className="font-medium text-fg">{fmtFechaCorta(d.periodo)}</span>
            </TD>
            <TD align="right" numeric>
              {fmtNumero(d.producido)}
            </TD>
            <TD align="right" numeric className="text-fg-secondary">
              {fmtNumero(d.meta)}
            </TD>
            <PctCell pct={d.cumpl_meta_pct} />
            <TD align="right" numeric className="text-fg-secondary">
              {d.paro_min > 0 ? fmtMinutos(d.paro_min) : '—'}
            </TD>
            <TD className="text-fg-secondary">{d.top_causal_nombre ?? '—'}</TD>
          </TR>
        ))}
      </TBody>
      <TFoot>
        <TR className="hover:bg-transparent">
          <TD className="font-medium text-fg">TOTAL</TD>
          <TD align="right" numeric className="font-medium text-fg">
            {fmtNumero(totales.producido)}
          </TD>
          <TD align="right" numeric>
            {fmtNumero(totales.meta)}
          </TD>
          <PctCell pct={totMeta} />
          <TD align="right" numeric>
            {totales.paro_min > 0 ? fmtMinutos(totales.paro_min) : '—'}
          </TD>
          <TD />
        </TR>
      </TFoot>
    </Table>
  );
}

// =============================================================================
// 4c. Tabla por hora (vista "Por horas" — NO es una tabla separada)
// =============================================================================

function TablaHora({
  serie,
  puedeCorregir,
  onEditar,
}: {
  serie: LineaTiempoSerieHora[];
  puedeCorregir: boolean;
  onEditar: (turnoId: string, fecha: string) => void;
}) {
  // El TOTAL solo suma filas válidas (las invalidadas "no suman").
  const totales = useMemo(() => {
    return serie
      .filter((h) => h.estado === 'valido')
      .reduce(
        (acc, h) => {
          acc.producido += h.producido;
          acc.meta += h.meta_h;
          acc.paro_min += h.paro_min;
          return acc;
        },
        { producido: 0, meta: 0, paro_min: 0 },
      );
  }, [serie]);

  if (serie.length === 0) {
    return <p className="py-10 text-center text-sm text-fg-tertiary">Sin capturas en el día.</p>;
  }

  const totPct = totales.meta > 0 ? (totales.producido / totales.meta) * 100 : null;
  // Columnas de peso solo si la respuesta las trae (línea con báscula).
  const conPeso = serie.some((h) => h.gramos_unidad_real !== undefined);
  const totSobrellenadoKg = serie
    .filter((h) => h.estado === 'valido' && typeof h.sobrellenado_kg === 'number')
    .reduce((acc, h) => acc + (h.sobrellenado_kg as number), 0);

  return (
    <Table maxHeight="70vh">
      <THead sticky>
        <TR>
          <TH>Hora</TH>
          <TH>SKU</TH>
          <TH align="right">Prod / Meta</TH>
          <TH align="right">%</TH>
          <TH align="right">Operarios</TH>
          <TH align="right">Golpes/min</TH>
          {conPeso ? (
            <>
              <TH align="right">g/u</TH>
              <TH align="right">Sobrellenado</TH>
            </>
          ) : null}
          <TH align="right">Paro</TH>
          <TH>Causal</TH>
          <TH>Validación</TH>
        </TR>
      </THead>
      <TBody>
        {serie.map((h, i) => {
          const invalidado = h.estado === 'invalidado';
          return (
            <TR
              key={`${h.hora}-${h.sku_codigo ?? ''}-${i}`}
              className={cn(invalidado && 'opacity-50')}
            >
              <TD>
                <span
                  className={cn(
                    'font-mono text-sm tabular-nums text-fg',
                    invalidado && 'line-through',
                  )}
                >
                  {h.hora}
                </span>
              </TD>
              <TD>
                <span className={cn('text-fg-secondary', invalidado && 'line-through')}>
                  {h.sku_codigo ?? '—'}
                </span>
              </TD>
              <TD align="right" numeric>
                <span className={cn('font-medium text-fg', invalidado && 'line-through')}>
                  {fmtNumero(h.producido)}
                </span>
                <span className="text-[11px] text-fg-tertiary">/{fmtNumero(h.meta_h)}</span>
              </TD>
              <PctCell pct={h.cumpl_meta_pct} strike={invalidado} />
              <TD align="right" numeric className="text-fg-secondary">
                {h.operarios ?? '—'}
              </TD>
              <TD align="right" numeric className="text-fg-secondary">
                {h.golpes_min !== null ? h.golpes_min.toFixed(0) : '—'}
              </TD>
              {conPeso ? (
                <>
                  <TD align="right" numeric className="text-fg-secondary">
                    {fmtGramos(h.gramos_unidad_real)}
                  </TD>
                  <TD
                    align="right"
                    numeric
                    className={claseTono(tonoSobrellenado(h.sobrellenado_g_unidad))}
                  >
                    {h.sobrellenado_kg === null || h.sobrellenado_kg === undefined ? (
                      <span className="text-[11px] text-fg-tertiary">sin dato</span>
                    ) : (
                      fmtKgSigno(h.sobrellenado_kg)
                    )}
                  </TD>
                </>
              ) : null}
              <TD align="right" numeric className="text-fg-secondary">
                {h.paro_min > 0 ? fmtMinutos(h.paro_min) : '—'}
              </TD>
              <TD className="text-fg-secondary">{h.causal_nombre ?? '—'}</TD>
              <TD>
                <span className="inline-flex flex-wrap items-center gap-1.5">
                  {invalidado ? (
                    <span className="text-[11px] font-medium text-fg-tertiary">no suma</span>
                  ) : h.validado ? (
                    <span className="text-[11px] font-medium text-ok">validado</span>
                  ) : (
                    <span className="text-[11px] text-warn">pendiente</span>
                  )}
                  {h.origen_correccion ? (
                    <Badge tone="info">Creado por corrección</Badge>
                  ) : h.corregido_at ? (
                    <Badge tone="info" title={h.razon_correccion ?? undefined}>
                      Corregido
                    </Badge>
                  ) : null}
                  {puedeCorregir && !invalidado && h.turno_id ? (
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-6 px-1.5"
                      title="Editar este registro"
                      onClick={() => onEditar(h.turno_id as string, h.fecha)}
                    >
                      <Icon name="edit" size={14} />
                    </Button>
                  ) : null}
                </span>
              </TD>
            </TR>
          );
        })}
      </TBody>
      <TFoot>
        <TR className="hover:bg-transparent">
          <TD className="font-medium text-fg">TOTAL</TD>
          <TD />
          <TD align="right" numeric className="font-medium text-fg">
            {fmtNumero(totales.producido)}
            <span className="text-[11px] text-fg-tertiary">/{fmtNumero(totales.meta)}</span>
          </TD>
          <PctCell pct={totPct} />
          <TD />
          <TD />
          {conPeso ? (
            <>
              <TD />
              <TD align="right" numeric className="font-medium text-fg">
                {fmtKgSigno(totSobrellenadoKg)}
              </TD>
            </>
          ) : null}
          <TD align="right" numeric>
            {totales.paro_min > 0 ? fmtMinutos(totales.paro_min) : '—'}
          </TD>
          <TD />
          <TD />
        </TR>
      </TFoot>
    </Table>
  );
}

function PctCell({
  pct,
  muted,
  strike,
}: {
  pct: number | null;
  muted?: boolean;
  strike?: boolean;
}) {
  const tone = semaforoPct(pct);
  return (
    <TD align="right" numeric>
      <span
        className={cn(
          muted ? 'text-fg-secondary' : cn('font-medium', toneText(tone)),
          strike && 'line-through',
        )}
      >
        {fmtPct(pct)}
      </span>
    </TD>
  );
}

// =============================================================================
// 5. Por SKU
// =============================================================================

function PorSku({ data }: { data: CoordinadorLineaTiempo }) {
  const { por_sku } = data;
  const conPeso = por_sku.some((s) => s.gramos_unidad_real !== undefined);

  // Peor SKU = menor %cumpl entre los que corrieron y tienen producción.
  const peorSkuId = useMemo(() => {
    const candidatos = por_sku.filter(
      (s) => s.corrio && s.producido > 0 && s.cumpl_meta_pct !== null,
    );
    if (candidatos.length === 0) return null;
    return candidatos.reduce((peor, s) =>
      (s.cumpl_meta_pct as number) < (peor.cumpl_meta_pct as number) ? s : peor,
    ).sku_id;
  }, [por_sku]);

  return (
    <Card>
      <CardContent className="flex flex-col gap-3 p-3">
        <h2 className="text-sm font-medium text-fg">Por SKU</h2>
        {por_sku.length === 0 ? (
          <p className="py-8 text-center text-sm text-fg-tertiary">Sin SKUs en el período.</p>
        ) : (
          <Table maxHeight="600px">
            <THead sticky>
              <TR>
                <TH>SKU</TH>
                <TH align="right">Producido</TH>
                <TH align="right">% cumpl</TH>
                <TH align="right">Golpes/min</TH>
                {conPeso ? (
                  <>
                    <TH align="right">g/u real · nominal</TH>
                    <TH align="right">Sobrellenado</TH>
                  </>
                ) : null}
                <TH align="right">Horas corridas</TH>
              </TR>
            </THead>
            <TBody>
              {por_sku.map((s) => {
                const esPeor = s.sku_id === peorSkuId;
                return (
                  <TR
                    key={s.sku_id}
                    className={cn(!s.corrio && 'opacity-50', esPeor && 'bg-crit/5')}
                  >
                    <TD>
                      <div className="flex items-center gap-1.5">
                        <span className="font-medium text-fg">{s.sku_codigo}</span>
                        {esPeor ? (
                          <span className="rounded bg-crit/15 px-1 py-0.5 text-[9px] font-medium uppercase tracking-wide text-crit">
                            peor
                          </span>
                        ) : null}
                        {!s.corrio ? (
                          <span className="text-[10px] text-fg-tertiary">no corrió</span>
                        ) : null}
                      </div>
                      <div className="text-[10.5px] text-fg-tertiary">{s.sku_descripcion}</div>
                    </TD>
                    <TD align="right" numeric>
                      {fmtNumero(s.producido)}
                    </TD>
                    <PctCell pct={s.cumpl_meta_pct} />
                    <TD align="right" numeric className="text-fg-secondary">
                      {s.golpes_aplica && s.golpes_real !== null
                        ? `${s.golpes_real.toFixed(0)}${
                            s.golpes_estandar !== null ? ` / ${s.golpes_estandar.toFixed(0)}` : ''
                          }`
                        : '—'}
                    </TD>
                    {conPeso ? (
                      <>
                        <TD align="right" numeric className="text-fg-secondary">
                          {fmtGramos(s.gramos_unidad_real)}
                          <span className="text-[11px] text-fg-tertiary">
                            {' · '}
                            {fmtGramos(s.peso_nominal_g)}
                          </span>
                        </TD>
                        <TD
                          align="right"
                          numeric
                          className={claseTono(tonoSobrellenado(s.sobrellenado_g_unidad))}
                        >
                          {s.sobrellenado_kg === null || s.sobrellenado_kg === undefined ? (
                            <span className="text-[11px] text-fg-tertiary">sin dato</span>
                          ) : (
                            fmtKgSigno(s.sobrellenado_kg)
                          )}
                        </TD>
                      </>
                    ) : null}
                    <TD align="right" numeric className="text-fg-secondary">
                      {fmtHoras(s.horas_corridas)}
                    </TD>
                  </TR>
                );
              })}
            </TBody>
          </Table>
        )}
      </CardContent>
    </Card>
  );
}

// =============================================================================
// 6. Paros · cuándo y por qué
// =============================================================================

function ParosSection({ data }: { data: CoordinadorLineaTiempo }) {
  const { timeline, pareto } = data.paros;

  return (
    <Card>
      <CardContent className="flex flex-col gap-4 p-3">
        <h2 className="text-sm font-medium text-fg">Paros · cuándo y por qué</h2>

        {timeline.length === 0 && pareto.length === 0 ? (
          <p className="py-8 text-center text-sm text-fg-tertiary">
            Sin paros registrados en el período. 🎉
          </p>
        ) : (
          <div className="grid grid-cols-1 gap-5 lg:grid-cols-2">
            <ParosTimeline timeline={timeline} />
            <ParosPareto pareto={pareto} />
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function ParosTimeline({ timeline }: { timeline: CoordinadorLineaTiempo['paros']['timeline'] }) {
  // Lista compacta ordenada por hora; ancho/color del bullet por duración.
  const ordenados = useMemo(
    () => [...timeline].sort((a, b) => a.hora_inicio.localeCompare(b.hora_inicio)),
    [timeline],
  );
  const maxMin = useMemo(() => ordenados.reduce((m, p) => Math.max(m, p.minutos), 0), [ordenados]);

  const fmtHora = (iso: string): string => {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return iso.slice(11, 16) || iso;
    return new Intl.DateTimeFormat('es-CO', {
      day: '2-digit',
      month: 'short',
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
    })
      .format(d)
      .replace(/\./g, '');
  };

  return (
    <div className="flex flex-col gap-2">
      <h3 className="text-[10px] font-medium uppercase tracking-[0.1em] text-fg-tertiary">
        Línea de tiempo
      </h3>
      {ordenados.length === 0 ? (
        <p className="py-6 text-center text-sm text-fg-tertiary">Sin paros.</p>
      ) : (
        // Altura acotada con scroll interno: con un mes de datos el timeline
        // tiene decenas de paros y haría la página interminable.
        <ul className="flex max-h-80 flex-col gap-2 overflow-y-auto pr-1">
          {ordenados.map((p, i) => {
            const tone: Tone = p.minutos >= 30 ? 'crit' : p.minutos >= 10 ? 'warn' : 'neutral';
            const ancho = maxMin > 0 ? Math.max(8, (p.minutos / maxMin) * 100) : 8;
            return (
              <li key={`${p.hora_inicio}-${i}`} className="flex items-center gap-2.5">
                <span className="w-28 flex-shrink-0 font-mono text-[11px] tabular-nums text-fg-tertiary">
                  {fmtHora(p.hora_inicio)}
                </span>
                <div className="h-3 flex-1 overflow-hidden rounded-full bg-surface-overlay">
                  <div
                    className={cn('h-full rounded-full', toneDot(tone))}
                    style={{ width: `${ancho}%` }}
                  />
                </div>
                <span className="w-12 flex-shrink-0 text-right text-[11px] tabular-nums text-fg-secondary">
                  {fmtMinutos(p.minutos)}
                </span>
                <span
                  className="hidden w-32 flex-shrink-0 items-center gap-1.5 truncate text-[11px] text-fg-tertiary sm:inline-flex"
                  title={`${p.causal_madre_nombre} · ${p.causal_nombre}`}
                >
                  <span
                    className="inline-block size-2 flex-shrink-0 rounded-full"
                    style={{ background: p.categoria_color ?? 'var(--color-fg-tertiary)' }}
                  />
                  <span className="truncate">{p.causal_madre_nombre}</span>
                </span>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}

function ParosPareto({ pareto }: { pareto: CoordinadorLineaTiempo['paros']['pareto'] }) {
  const chartData = useMemo(
    () =>
      [...pareto]
        .sort((a, b) => b.minutos - a.minutos)
        .map((p) => ({
          causal: p.causal_madre_nombre,
          minutos: Math.round(p.minutos),
          paros_count: p.paros_count,
          // Color de la categoría del catálogo (ej. Paros Programados = azul).
          color: p.categoria_color ?? 'var(--color-fg-tertiary)',
        })),
    [pareto],
  );

  return (
    <div className="flex flex-col gap-2">
      <h3 className="text-[10px] font-medium uppercase tracking-[0.1em] text-fg-tertiary">
        Pareto por causal madre (minutos)
      </h3>
      {chartData.length === 0 ? (
        <p className="py-6 text-center text-sm text-fg-tertiary">Sin paros.</p>
      ) : (
        <div className="w-full" style={{ height: Math.max(160, chartData.length * 36) }}>
          <ResponsiveContainer width="100%" height="100%">
            <BarChart
              data={chartData}
              layout="vertical"
              margin={{ top: 4, right: 56, left: 8, bottom: 4 }}
            >
              <CartesianGrid
                strokeDasharray="3 3"
                stroke="var(--color-border)"
                horizontal={false}
              />
              <XAxis
                type="number"
                tick={{ fontSize: 10, fill: 'var(--color-fg-tertiary)' }}
                axisLine={false}
                tickLine={false}
                tickFormatter={(v) => fmtNumero(Number(v))}
              />
              <YAxis
                type="category"
                dataKey="causal"
                width={120}
                tick={{ fontSize: 10.5, fill: 'var(--color-fg-secondary)' }}
                axisLine={false}
                tickLine={false}
              />
              <Tooltip
                cursor={{ fill: 'var(--color-surface-overlay)', fillOpacity: 0.4 }}
                contentStyle={{
                  background: 'var(--color-surface-elevated)',
                  border: '1px solid var(--color-border)',
                  borderRadius: 8,
                  fontSize: 12,
                }}
                formatter={(value, _n, item) => [
                  `${fmtMinutos(Number(value))} · ${item?.payload?.paros_count ?? 0} eventos`,
                  'Paro',
                ]}
              />
              <Bar dataKey="minutos" radius={[0, 3, 3, 0]} fillOpacity={0.85}>
                {chartData.map((d, i) => (
                  <Cell key={i} fill={d.color} />
                ))}
                <LabelList
                  content={
                    <EtiquetaValor
                      formatter={(v) => fmtMinutos(v)}
                      total={chartData.length}
                      horizontal
                    />
                  }
                />
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}
    </div>
  );
}

// =============================================================================
// 7. Personal · líderes/operarios que operaron la línea
// =============================================================================

function PersonalSection({ data }: { data: CoordinadorLineaTiempo }) {
  const { personal } = data;

  return (
    <Card>
      <CardContent className="flex flex-col gap-3 p-3">
        <h2 className="text-sm font-medium text-fg">Personal</h2>
        {personal.length === 0 ? (
          <p className="py-8 text-center text-sm text-fg-tertiary">
            Sin personal registrado en el período.
          </p>
        ) : (
          <Table maxHeight="600px">
            <THead sticky>
              <TR>
                <TH>Operario</TH>
                <TH align="right">Producido</TH>
                <TH align="right">% cumpl</TH>
                <TH align="right">Horas</TH>
                <TH align="right">Validados</TH>
              </TR>
            </THead>
            <TBody>
              {personal.map((p) => {
                const tone = semaforoPct(p.cumpl_meta_pct);
                return (
                  <TR key={p.user_id}>
                    <TD>
                      <span className="font-medium text-fg">
                        {p.nombre} {p.apellido}
                      </span>
                    </TD>
                    <TD align="right" numeric>
                      {fmtNumero(p.producido)}
                    </TD>
                    <TD align="right" numeric>
                      <span className={cn('font-medium', toneText(tone))}>
                        {fmtPct(p.cumpl_meta_pct)}
                      </span>
                    </TD>
                    <TD align="right" numeric className="text-fg-secondary">
                      {p.bloques_capturados}
                    </TD>
                    <TD align="right" numeric className="text-fg-secondary">
                      {p.validados}/{p.total_registros}
                    </TD>
                  </TR>
                );
              })}
            </TBody>
          </Table>
        )}
      </CardContent>
    </Card>
  );
}
